// @ts-strict-ignore
import React from 'react'
import { graphql, useFragment } from 'react-relay'
import { Chip, Divider, Grid, Stack, Typography, Button } from '@mui/material'
import Box from 'components/Box'
import Condition from 'components/Condition'
import StyledLink from 'components/StyledLink'
import { BlogPostContent_BlogPost$key } from '__generated__/BlogPostContent_BlogPost.graphql'
import { useMediaQuery } from 'hooks/useMediaQuery'
import HtmlRenderer from 'components/HtmlRenderer'
import MetaInfo from './components/MetaInfo'

export interface IBlogPostContentProps {
  blogPostFragmentRef: BlogPostContent_BlogPost$key
}

function BlogPostContent(props: IBlogPostContentProps) {
  const { blogPostFragmentRef } = props
  const isSmallScreen = useMediaQuery(t => t.breakpoints.down('md'))

  const blogPost = useFragment(
    graphql`
      fragment BlogPostContent_BlogPost on BlogPost {
        user {
          id
          self
        }
        slug
        status
        title
        content
        blogCategories {
          name
          slug
        }
        blogSubcategories {
          name
          slug
          blogCategory {
            slug
          }
        }
        ...MetaInfo_BlogPostFragment
      }
    `,
    blogPostFragmentRef,
  )

  const isPublished = ((blogPost?.status === 'published' || blogPost?.status === 'approved'))

  return (
    <div>
      <Box variant="wide">
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h2">
              {blogPost?.title}
            </Typography>
          </Grid>

          {blogPost?.user?.self && (
            <Grid item>
              <Button component={StyledLink} href={`/blog/post/${blogPost.slug}/edit`} color="primary">
                Edit {isPublished ? 'Post' : 'Draft'}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              {blogPost?.blogCategories?.map(blogCategory => (
                <StyledLink key={blogCategory.slug} href={`/blog/category/${blogCategory.slug}`} style={{ textDecoration: 'none' }}>
                  <Chip label={blogCategory.name} style={{ cursor: 'pointer' }} size="small" />
                </StyledLink>
              ))}

              {blogPost?.blogSubcategories?.map(blogSubcategory => (
                <StyledLink key={blogSubcategory.slug} href={`/blog/category/${blogSubcategory?.blogCategory?.slug}/${blogSubcategory?.slug}`} style={{ textDecoration: 'none' }}>
                  <Chip label={blogSubcategory?.name} style={{ cursor: 'pointer' }} size="small" />
                </StyledLink>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Divider style={{ margin: 0 }} />

      <Condition condition={!isSmallScreen}>
        <MetaInfo blogPostFragment={blogPost} />
        <Divider style={{ margin: 0 }} />
      </Condition>

      <Box variant="wide">
        <HtmlRenderer source={blogPost?.content} compact />
      </Box>

      <Condition condition={isSmallScreen}>
        <Divider style={{ margin: 0 }} />
        <MetaInfo blogPostFragment={blogPost} />
      </Condition>
    </div>
  )
}

export default React.memo(BlogPostContent)
